// =============================================================================
// DefectDetailModal.tsx — HOP Slice 4. Open a defect to manage it fully:
// edit category / severity / who-goes-back / ETA, change status with a comment
// (audited), and see the raised → closed timeline + time-to-close. Close it
// internally (client-side close from the portal is a separate, later step).
// =============================================================================

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, AlertTriangle, User as UserIcon, Save, Clock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Pill, Spinner } from "@/components/ui";
import { format, parseISO, differenceInCalendarDays } from "date-fns";

type Status = "open" | "in_progress" | "awaiting_reinspection" | "closed" | "cancelled";
const STATUS_LABEL: Record<Status, string> = { open: "Open", in_progress: "In progress", awaiting_reinspection: "Awaiting re-inspection", closed: "Closed", cancelled: "Cancelled" };
const STATUS_TONE: Record<Status, any> = { open: "critical", in_progress: "pending", awaiting_reinspection: "notice", closed: "approved", cancelled: "default" };
const ALL_STATUSES: Status[] = ["open", "in_progress", "awaiting_reinspection", "closed", "cancelled"];
const CATEGORIES = ["gland", "ground", "rigging", "civil", "structural", "electrical", "other"];
const SEVERITIES = ["minor", "major", "critical"];

export default function DefectDetailModal({ defectId, onClose, onChanged }: { defectId: string; onClose: () => void; onChanged?: () => void }) {
  const user = useRequireAuth();
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");

  const { data: d, refetch } = useQuery({
    queryKey: ["defect_detail", defectId],
    queryFn: async () => {
      const { data } = await supabase.from("site_defects").select(`
        defect_id, defect_number, category, severity, title, description, status, source, client_contact,
        due_date, raised_at, closed_at, assigned_to_employee_id,
        project:project_id ( project_code, project_name ), site:site_id ( site_name ), pack:pack_id ( pack_number )
      `).eq("defect_id", defectId).single();
      return data as any;
    },
  });

  const { data: history = [] } = useQuery({
    queryKey: ["defect_history", defectId],
    queryFn: async () => {
      const { data } = await supabase.from("defect_status_history")
        .select("history_id, changed_at, old_status, new_status, note, changed_by:changed_by ( employees:employee_id ( first_name, last_name ) )")
        .eq("defect_id", defectId).order("changed_at", { ascending: true });
      return (data ?? []) as any[];
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["defect_employees"],
    queryFn: async () => {
      const { data } = await supabase.from("employees").select("employee_id, first_name, last_name").eq("is_active", true).order("first_name");
      return (data ?? []) as any[];
    },
  });

  // Local editable copies (seeded once d loads)
  const [edit, setEdit] = useState<any>(null);
  const cur = edit ?? d;
  if (!d) return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg p-8" onClick={e => e.stopPropagation()}><Spinner size={20} /></div>
    </div>
  );

  const dirty = edit && (edit.category !== d.category || edit.severity !== d.severity || edit.assigned_to_employee_id !== d.assigned_to_employee_id || edit.due_date !== d.due_date);

  async function saveFields() {
    setBusy(true);
    try {
      await supabase.from("site_defects").update({
        category: cur.category, severity: cur.severity,
        assigned_to_employee_id: cur.assigned_to_employee_id || null,
        due_date: cur.due_date || null,
      }).eq("defect_id", defectId);
      setEdit(null); await refetch(); onChanged?.();
    } finally { setBusy(false); }
  }

  async function changeStatus(next: Status) {
    setBusy(true);
    try {
      const patch: any = { status: next };
      if (next === "closed") { patch.closed_at = new Date().toISOString(); patch.closed_by = user?.user_id ?? null; }
      if (d.status === "closed" && next !== "closed") { patch.closed_at = null; patch.closed_by = null; }
      await supabase.from("site_defects").update(patch).eq("defect_id", defectId);
      await supabase.from("defect_status_history").insert({ defect_id: defectId, changed_by: user?.user_id ?? null, old_status: d.status, new_status: next, note: comment.trim() || null });
      setComment(""); await refetch(); onChanged?.();
    } finally { setBusy(false); }
  }

  const daysOpen = d.closed_at ? differenceInCalendarDays(new Date(d.closed_at), new Date(d.raised_at)) : differenceInCalendarDays(new Date(), new Date(d.raised_at));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center p-0 md:p-4" onClick={onClose}>
      <div className="bg-ink-card border-t md:border border-line w-full md:max-w-2xl md:rounded-lg max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-[13px] text-accent">{d.defect_number}</span>
            <Pill label={STATUS_LABEL[d.status as Status]} tone={STATUS_TONE[d.status as Status]} small />
            {d.source === "client" && <Pill label="client-raised" tone="bid" small />}
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text p-1"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            {d.title && <div className="text-[15px] font-semibold text-text">{d.title}</div>}
            <div className="text-[13px] text-text whitespace-pre-wrap mt-0.5">{d.description}</div>
            <div className="text-[11px] text-text-muted mt-1.5 font-mono">{d.project?.project_code}{d.site?.site_name ? ` · ${d.site.site_name}` : ""}{d.pack?.pack_number ? ` · ${d.pack.pack_number}` : ""}</div>
            {d.client_contact && <div className="text-[11px] text-text-muted mt-0.5">Reported by: {d.client_contact}</div>}
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-[11px] text-text-muted">Category
              <select value={cur.category} onChange={e => setEdit({ ...cur, category: e.target.value })} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text capitalize">
                {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </label>
            <label className="block text-[11px] text-text-muted">Severity
              <select value={cur.severity} onChange={e => setEdit({ ...cur, severity: e.target.value })} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text capitalize">
                {SEVERITIES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </label>
            <label className="block text-[11px] text-text-muted">Who goes back
              <select value={cur.assigned_to_employee_id ?? ""} onChange={e => setEdit({ ...cur, assigned_to_employee_id: e.target.value })} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text">
                <option value="">Unassigned</option>
                {employees.map((e2: any) => <option key={e2.employee_id} value={e2.employee_id}>{e2.first_name} {e2.last_name}</option>)}
              </select>
            </label>
            <label className="block text-[11px] text-text-muted">ETA to resolve
              <input type="date" value={cur.due_date ?? ""} onChange={e => setEdit({ ...cur, due_date: e.target.value })} className="mt-1 w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text" />
            </label>
          </div>
          {dirty && <div className="flex justify-end"><Button size="sm" icon={Save} onClick={saveFields} disabled={busy}>Save changes</Button></div>}

          {/* Metrics */}
          <div className="flex items-center gap-4 text-[11px] text-text-muted bg-ink-raised border border-line rounded-md px-3 py-2 flex-wrap">
            <span>Raised {format(parseISO(d.raised_at), "dd MMM yyyy")}</span>
            {d.closed_at && <span>Closed {format(parseISO(d.closed_at), "dd MMM yyyy")}</span>}
            <span className="inline-flex items-center gap-1"><Clock size={11} /> {d.closed_at ? `${daysOpen}d to close` : `${daysOpen}d open`}</span>
          </div>

          {/* Status change */}
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium mb-1.5">Change status</div>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} placeholder="Optional note on the change (e.g. what was done, or why closing)."
              className="w-full bg-ink border border-line rounded px-2.5 py-2 text-[13px] text-text mb-2" />
            <div className="flex flex-wrap gap-1.5">
              {ALL_STATUSES.filter(s => s !== d.status).map(s => (
                <button key={s} onClick={() => changeStatus(s)} disabled={busy}
                  className={`text-[12px] px-2.5 py-1.5 rounded border transition-colors disabled:opacity-60 ${s === "closed" ? "border-ok/40 text-ok hover:bg-ok/10" : "border-line text-text-muted hover:border-accent hover:text-accent"}`}>
                  → {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-medium mb-1.5">History</div>
            <div className="space-y-1.5">
              {history.map((h: any) => (
                <div key={h.history_id} className="text-[12px] text-text-muted flex items-start gap-2">
                  <span className="font-mono text-text-dim flex-shrink-0">{format(parseISO(h.changed_at), "dd MMM HH:mm")}</span>
                  <span>
                    {h.old_status ? `${STATUS_LABEL[h.old_status as Status] ?? h.old_status} → ` : ""}<span className="text-text">{STATUS_LABEL[h.new_status as Status] ?? h.new_status}</span>
                    {h.changed_by?.employees && <span className="text-text-dim"> · {h.changed_by.employees.first_name} {h.changed_by.employees.last_name}</span>}
                    {h.note && <span className="block text-text-muted italic">“{h.note}”</span>}
                  </span>
                </div>
              ))}
              {history.length === 0 && <div className="text-[12px] text-text-dim italic">No changes yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
